import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { User } from 'firebase/auth'

import {
	AgentAnalysisParams,
	CallsListParams,
	fetchAgentAnalysis,
	fetchCallById,
	fetchCallsList,
	fetchTranscriptAnalysis,
	fetchTranscriptComparison,
	TranscriptAnalysisParams,
	TranscriptComparisonParams,
} from 'services/analysisService'
import {
	CallListResponse,
	TranscriptAnalysisResult,
	UserAgentComparisonResult,
} from 'types/server/analysis'
import { CallInsight } from 'types/server/call-insights'

/**
 * Hook for fetching transcript analysis
 *
 * @param user - Firebase user for authentication
 * @param params - Query parameters for filtering and configuration
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with transcript analysis data
 */
export const useTranscriptAnalysis = (
	user: User | null,
	params: TranscriptAnalysisParams = {},
	enabled: boolean = true
): UseQueryResult<TranscriptAnalysisResult, Error> => {
	return useQuery({
		queryKey: ['transcript-analysis', params],
		queryFn: () => {
			if (!user) throw new Error('User not authenticated')
			return fetchTranscriptAnalysis(user, params)
		},
		enabled: enabled && !!user,
	})
}

/**
 * Hook for fetching transcript comparison between users and agents
 *
 * @param user - Firebase user for authentication
 * @param params - Query parameters for filtering
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with comparison data
 */
export const useTranscriptComparison = (
	user: User | null,
	params: TranscriptComparisonParams = {},
	enabled: boolean = true
): UseQueryResult<UserAgentComparisonResult, Error> => {
	return useQuery({
		queryKey: ['transcript-comparison', params],
		queryFn: () => {
			if (!user) throw new Error('User not authenticated')
			return fetchTranscriptComparison(user, params)
		},
		enabled: enabled && !!user,
	})
}

/**
 * Hook for fetching agent-specific analysis
 *
 * @param user - Firebase user for authentication
 * @param agentId - The agent ID to analyse
 * @param params - Optional query parameters
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with agent analysis data
 */
export const useAgentAnalysis = (
	user: User | null,
	agentId: string,
	params: AgentAnalysisParams = {},
	enabled: boolean = true
): UseQueryResult<TranscriptAnalysisResult, Error> => {
	return useQuery({
		queryKey: ['agent-analysis', agentId, params],
		queryFn: () => {
			if (!user) throw new Error('User not authenticated')
			return fetchAgentAnalysis(user, agentId, params)
		},
		enabled: enabled && !!user && !!agentId,
	})
}

/**
 * Hook for fetching calls list
 *
 * @param user - Firebase user for authentication
 * @param params - Query parameters for filtering
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with calls list
 */
export const useCallsList = (
	user: User | null,
	params: CallsListParams = {},
	enabled: boolean = true
): UseQueryResult<CallListResponse, Error> => {
	return useQuery({
		queryKey: ['calls-list', params],
		queryFn: () => {
			if (!user) throw new Error('User not authenticated')
			return fetchCallsList(user, params)
		},
		enabled: enabled && !!user,
	})
}

/**
 * Hook for fetching a specific call by ID
 *
 * @param user - Firebase user for authentication
 * @param callId - The call ID to retrieve
 * @param enabled - Whether the query should run (default: true)
 * @returns React Query result with call data
 */
export const useCall = (
	user: User | null,
	callId: string,
	enabled: boolean = true
): UseQueryResult<CallInsight, Error> => {
	return useQuery({
		queryKey: ['call', callId],
		queryFn: () => {
			if (!user) throw new Error('User not authenticated')
			return fetchCallById(user, callId)
		},
		enabled: enabled && !!user && !!callId,
	})
}
